<template>
  <div class="card text-dark mb-3">
    <div class="card-header dashboard title-style big">
      {{ $t('flexigo.dashboard.systemStatus') }}
    </div>

    <div class="card-body">
      <div class="row">
        <h6 class="title-style float-start">{{ $t('flexigo.common.routes') }}</h6>
        <div class="col position-absolute end-0" style="margin-top: -15px">
          <DateComponent v-model="selectedDate" class="float-end" @date-changed="onChange" @show-custom-range="onCustomRangeChange" />
        </div>
      </div>
      <div v-if="showCustomRange" class="row mb-3">
        <div class="col-12 d-flex justify-content-end">
          <date-range-picker
            :start="startDate"
            :end="endDate"
            :initial-show="false"
            :locale="$i18n.locale"
            horizontal="right"
            @selected="onCustomDateSelected"
          />
        </div>
      </div>
      <div class="row">
        <div class="row ms-0">
          <toggle-button
            v-model="toggleButtonValue"
            class="mb-3"
            :first-label="$t('flexigo.dashboard.toCampus')"
            :second-label="$t('flexigo.dashboard.fromCampus')"
          />
        </div>
      </div>

      <!-- toggleButtonValue === true means to campus -->
      <div v-if="toggleButtonValue">
        <div class="row">
          <div class="col">
            <user-info
              :subtitle="this.$t('flexigo.dashboard.onTimeArrival')"
              :amount="onTimeArrivalToCampus"
              :link="{
                name: 'RouteStatuses',
                params: { type: 'SHUTTLE' },
                query: { routeInstanceState: 'ARRIVED_ON_TIME', fromDate: fromDate, toDate: toDate, direction: 'inbound' },
              }"
            />
          </div>
          <div class="col">
            <user-info
              :subtitle="this.$t('flexigo.dashboard.noArrival')"
              :amount="noArrivalToCampus"
              :link="{
                name: 'RouteStatuses',
                params: { type: 'SHUTTLE' },
                query: { routeInstanceState: 'NOT_ARRIVED', fromDate: fromDate, toDate: toDate, direction: 'inbound' },
              }"
            />
          </div>
        </div>
        <div class="row">
          <div class="col">
            <user-info
              :subtitle="this.$t('flexigo.dashboard.lateArrival')"
              :amount="lateArrivalToCampus"
              :link="{
                name: 'RouteStatuses',
                params: { type: 'SHUTTLE' },
                query: { routeInstanceState: 'ARRIVED_LATE', fromDate: fromDate, toDate: toDate, direction: 'inbound' },
              }"
            />
          </div>
          <div class="col">
            <user-info
              :subtitle="this.$t('flexigo.dashboard.deviceProblem')"
              :amount="deviceProblemToCampus"
              :link="{
                name: 'RouteStatuses',
                params: { type: 'SHUTTLE' },
                query: { routeInstanceState: 'DEVICE_NOT_OK', fromDate: fromDate, toDate: toDate, direction: 'inbound' },
              }"
            />
          </div>
        </div>
        <div class="row">
          <div class="d-flex align-items-center mb-1">
            <h6 class="title-style mb-0">{{ $t('flexigo.dashboard.devices') }}</h6>
            <a
              href="#"
              class="ms-2"
              :class="{ 'opacity-50': isDownloadingReport }"
              @click.prevent="downloadReport({ reportType: 'vehicleList' })"
            >
              <img
                :src="`${baseUrl}icons/download.svg`"
                class="has-cursor-pointer"
                style="width: 16px; height: 16px"
                alt="Download"
              />
            </a>
          </div>
          <div class="col">
            <user-info :subtitle="this.$t('flexigo.dashboard.availableDevice')" :amount="availableDevice" />
          </div>
        </div>
        <div class="row">
          <div class="col">
            <user-info :subtitle="this.$t('flexigo.dashboard.notAvailableDevice')" :amount="notAvailableDevice" />
          </div>
          <div class="col">
            <user-info :subtitle="this.$t('flexigo.dashboard.noSignal')" :amount="noSignal" />
          </div>
        </div>
        <div class="row">
          <h6 class="title-style float-start">{{ $t('flexigo.dashboard.userRegistration') }}</h6>
          <div class="col">
            <user-info
              :subtitle="this.$t('flexigo.dashboard.undefinedRoute')"
              :amount="undefinedRoute"
              :color="'#f5b548'"
              :link="isAdminOrSuperAdmin ? { name: 'EmployeeList', query: { undefinedRoute: true } } : null"
            />
          </div>
          <div class="col">
            <user-info
              :subtitle="this.$t('flexigo.dashboard.addressMissing')"
              :amount="addressMissing"
              :color="'#f5b548'"
              :link="isAdminOrSuperAdmin ? { name: 'EmployeeList', query: { addressMissing: true } } : null"
            />
          </div>
        </div>
        <div class="row">
          <div class="col">
            <user-info
              :subtitle="this.$t('flexigo.dashboard.notUsingShuttle')"
              :amount="notUsingShuttle"
              :link="isAdminOrSuperAdmin ? { name: 'EmployeeList', query: { isUsingService: false } } : null"
            />
          </div>
          <div class="col">
            <user-info :subtitle="this.$t('flexigo.dashboard.completed')" :amount="completed" />
          </div>
        </div>
        <div class="route-list-navbar bg-white has-shadow border-0 m-0">
          <ul class="badge-icon">
            <li>
              <a
                href="#"
                :class="{ active: selectedTab === $t('flexigo.dashboard.vehicles') }"
                @click.prevent="selectedTab = $t('flexigo.dashboard.vehicles')"
              >
                {{ $t('flexigo.dashboard.vehicles') }}
                <span v-if="vehicleAgeLimit" class="red-dot" />
              </a>
            </li>
            <li>
              <a
                href="#"
                :class="{ active: selectedTab === $t('flexigo.dashboard.drivers') }"
                @click.prevent="selectedTab = $t('flexigo.dashboard.drivers')"
              >
                {{ $t('flexigo.dashboard.drivers') }}
                <span v-if="driverAgeLimit" class="red-dot" />
              </a>
            </li>
            <li>
              <a
                href="#"
                :class="{ active: selectedTab === $t('flexigo.dashboard.documents') }"
                @click.prevent="selectedTab = $t('flexigo.dashboard.documents')"
              >
                {{ $t('flexigo.dashboard.documents') }}
                <span v-if="missingCount" class="red-dot" />
              </a>
            </li>
          </ul>
        </div>

        <div class="system-status-seperator" />

        <user-info
          v-if="selectedTab === $t('flexigo.dashboard.vehicles')"
          :subtitle="$t('flexigo.dashboard.overTheAgeLimit')"
          :amount="vehicleAgeLimit"
          :report-config="{ reportType: 'vehicleList', params: { filterOlderThanAgeLimit: true } }"
          @download-report="downloadReport"
        />
        <user-info
          v-if="selectedTab === $t('flexigo.dashboard.drivers')"
          :subtitle="$t('flexigo.dashboard.overTheAgeLimit')"
          :amount="driverAgeLimit"
          :report-config="{
            reportType: 'driver/report/card',
            params: {
              overAgeLimit: true,
              startDay: formatDateForUrl(fromDate),
              endDay: formatDateForUrl(toDate),
            },
          }"
          @download-report="downloadReport"
        />
        <user-info
          v-if="selectedTab === $t('flexigo.dashboard.documents')"
          :subtitle="this.$t('flexigo.dashboard.missing')"
          :amount="missingCount"
          :report-config="{ reportType: 'document/missing' }"
          @download-report="downloadReport"
        />
      </div>

      <!-- toggleButtonValue === true means from campus -->
      <div v-if="!toggleButtonValue">
        <div class="row">
          <div class="col">
            <user-info
              :subtitle="this.$t('flexigo.dashboard.leaveOnTime')"
              :amount="leaveOnTimeFromCampus"
              :link="{
                name: 'RouteStatuses',
                params: { type: 'SHUTTLE' },
                query: { routeInstanceState: 'LEAVE_ON_TIME', fromDate: fromDate, toDate: toDate, direction: 'outbound' },
              }"
            />
          </div>
          <div class="col">
            <user-info
              :subtitle="this.$t('flexigo.dashboard.readyToLeave')"
              :amount="readyToLeaveFromCampus"
              :link="{
                name: 'RouteStatuses',
                params: { type: 'SHUTTLE' },
                query: { routeInstanceState: 'READY_TO_LEAVE', fromDate: fromDate, toDate: toDate, direction: 'outbound' },
              }"
            />
          </div>
        </div>
        <div class="row">
          <div class="col">
            <user-info
              :subtitle="this.$t('flexigo.dashboard.leaveEarly')"
              :amount="leaveEarlyToCampus"
              :link="{
                name: 'RouteStatuses',
                params: { type: 'SHUTTLE' },
                query: { routeInstanceState: 'LEAVE_EARLY', fromDate: fromDate, toDate: toDate, direction: 'outbound' },
              }"
            />
          </div>
          <div class="col">
            <user-info
              :subtitle="this.$t('flexigo.dashboard.deviceProblem')"
              :amount="deviceProblemFromCampus"
              :link="{
                name: 'RouteStatuses',
                params: { type: 'SHUTTLE' },
                query: { routeInstanceState: 'DEVICE_NOT_OK', fromDate: fromDate, toDate: toDate, direction: 'outbound' },
              }"
            />
          </div>
        </div>
        <div class="row">
          <div class="d-flex align-items-center mb-1">
            <h6 class="title-style mb-0">{{ $t('flexigo.dashboard.devices') }}</h6>
            <a
              href="#"
              class="ms-2"
              :class="{ 'opacity-50': isDownloadingReport }"
              @click.prevent="downloadReport({ reportType: 'vehicleList' })"
            >
              <img
                :src="`${baseUrl}icons/download.svg`"
                class="has-cursor-pointer"
                style="width: 16px; height: 16px"
                alt="Download"
              />
            </a>
          </div>
          <div class="col">
            <user-info :subtitle="this.$t('flexigo.dashboard.availableDevice')" :amount="availableDevice" />
          </div>
        </div>
        <div class="row">
          <div class="col">
            <user-info :subtitle="this.$t('flexigo.dashboard.notAvailableDevice')" :amount="notAvailableDevice" />
          </div>
          <div class="col">
            <user-info :subtitle="this.$t('flexigo.dashboard.noSignal')" :amount="noSignal" />
          </div>
        </div>
        <div class="row">
          <h6 class="title-style float-start">{{ $t('flexigo.dashboard.userRegistration') }}</h6>
          <div class="col">
            <user-info
              :subtitle="this.$t('flexigo.dashboard.undefinedRoute')"
              :amount="undefinedRoute"
              :color="'#f5b548'"
              :link="isAdminOrSuperAdmin ? { name: 'EmployeeList', query: { undefinedRoute: true } } : null"
            />
          </div>
          <div class="col">
            <user-info
              :subtitle="this.$t('flexigo.dashboard.addressMissing')"
              :amount="addressMissing"
              :color="'#f5b548'"
              :link="isAdminOrSuperAdmin ? { name: 'EmployeeList', query: { addressMissing: true } } : null"
            />
          </div>
        </div>
        <div class="row">
          <div class="col">
            <user-info
              :subtitle="this.$t('flexigo.dashboard.notUsingShuttle')"
              :amount="notUsingShuttle"
              :link="isAdminOrSuperAdmin ? { name: 'EmployeeList', query: { isUsingService: false } } : null"
            />
          </div>
          <div class="col">
            <user-info :subtitle="this.$t('flexigo.dashboard.completed')" :amount="completed" />
          </div>
        </div>
        <div class="row">
          <h6 class="title-style float-start">{{ $t('flexigo.dashboard.documents') }}</h6>
          <div class="col">
            <user-info :subtitle="this.$t('flexigo.dashboard.missing')" :amount="missingCount" />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { format } from 'date-fns';
import DateRangePicker from '@vektor/date-picker';
import UserInfo from '../userInfo/userInfo.vue';
import ToggleButton from '../toggleButton/toggleButton.vue';
import DateComponent from '../date.vue';
import reports from '../../services/reports';
import toaster from '../../mixins/toaster';

export default {
  name: 'SystemStatus',
  components: { UserInfo, ToggleButton, DateComponent, DateRangePicker },
  mixins: [toaster],
  props: {
    documents: { type: Array, default: () => [] },
    userRegistration: { type: Array, default: () => [] },
    devices: { type: Array, default: () => [] },
    routes: { type: Array, default: () => [] },
    vehicleAgeLimit: {
      type: Number,
      default: 0,
    },
    driverAgeLimit: {
      type: Number,
      default: 0,
    },
  },
  data() {
    return {
      selectedDate: undefined,
      toggleButtonValue: true,
      tabName: 'toCampus',
      selectedTab: this.$t('flexigo.dashboard.vehicles'),
      dateRange: undefined,
      isDownloadingReport: false,
      showCustomRange: false,
      startDate: new Date(),
      endDate: new Date(),
    };
  },
  computed: {
    isAdminOrSuperAdmin() {
      const accountType = this.$store.state.userDetails?.accountType;
      return accountType === 'ADMIN' || accountType === 'FLEXIGO_VEKTOR_ADMIN';
    },
    // USER REGISTRATION
    missingCount() {
      return this.documents
        ? this.documents.reduce((acc, current) => acc + (current.state === 'MISSING' ? current.count : 0), 0)
        : 0;
    },
    undefinedRoute() {
      return this.userRegistration?.find((item) => item.state === 'NO_ROUTE')?.count ?? 0;
    },
    addressMissing() {
      return this.userRegistration?.find((item) => item.state === 'NO_HOME')?.count ?? 0;
    },
    notUsingShuttle() {
      return this.userRegistration?.find((item) => item.state === 'NOT_USING_ROUTE')?.count ?? 0;
    },
    completed() {
      return this.userRegistration?.find((item) => item.state === 'DEFINITION_OK')?.count ?? 0;
    },

    // DEVICES
    availableDevice() {
      return this.devices?.[0]?.deviceOk ?? 0;
    },
    notAvailableDevice() {
      return this.devices?.[0]?.noDevice ?? 0;
    },
    noSignal() {
      return this.devices?.[0]?.noSignal ?? 0;
    },

    // ROUTES
    onTimeArrivalToCampus() {
      return this.routes?.find((item) => item.state === 'ARRIVED_ON_TIME' && item.routeDirection === 0)?.count ?? 0;
    },
    leaveOnTimeFromCampus() {
      return this.routes?.find((item) => item.state === 'LEAVE_ON_TIME' && item.routeDirection === 1)?.count ?? 0;
    },
    noArrivalToCampus() {
      return this.routes?.find((item) => item.state === 'NOT_ARRIVED' && item.routeDirection === 0)?.count ?? 0;
    },
    readyToLeaveFromCampus() {
      return this.routes?.find((item) => item.state === 'READY_TO_LEAVE' && item.routeDirection === 1)?.count ?? 0;
    },
    lateArrivalToCampus() {
      return this.routes?.find((item) => item.state === 'ARRIVED_LATE' && item.routeDirection === 0)?.count ?? 0;
    },
    leaveEarlyToCampus() {
      return this.routes?.find((item) => item.state === 'LEAVE_EARLY' && item.routeDirection === 1)?.count ?? 0;
    },
    deviceProblemToCampus() {
      return this.routes?.find((item) => item.state === 'DEVICE_NOT_OK' && item.routeDirection === 0)?.count ?? 0;
    },
    deviceProblemFromCampus() {
      return this.routes?.find((item) => item.state === 'DEVICE_NOT_OK' && item.routeDirection === 1)?.count ?? 0;
    },
    fromDate() {
      return this.dateRange?.fromDate ?? format(new Date(), 'yyyy-MM-dd');
    },
    toDate() {
      return this.dateRange?.toDate ?? format(new Date(), 'yyyy-MM-dd');
    },
  },
  methods: {
    getLink(route, query = '') {
      const link = this.$router.resolve({
        name: 'legacy',
        params: { route },
      }).href;
      return `${link}${query ? `?${query}` : ''}`;
    },
    onChange(payload) {
      // Also update startDate/endDate to keep them in sync
      if (payload.fromDate) {
        this.startDate = new Date(payload.fromDate);
        this.endDate = new Date(payload.toDate);
      }
      this.$emit('selected-date', payload);
      this.dateRange = payload;
    },
    onCustomRangeChange(isCustomRange) {
      this.showCustomRange = isCustomRange;
    },
    onCustomDateSelected(dates) {
      // Validate that we have valid dates structure
      if (!dates || !dates.raw || !dates.raw.startDate || !dates.raw.endDate) {
        console.warn('Invalid dates received from date picker:', dates);
        return;
      }
      
      // Extract the raw Date objects from the date picker response
      const { startDate, endDate } = dates.raw;
      
      // Check if dates are valid
      if (Number.isNaN(startDate.getTime()) || Number.isNaN(endDate.getTime())) {
        console.warn('Invalid date values:', dates);
        return;
      }
      
      // Update internal date state
      this.startDate = startDate;
      this.endDate = endDate;
      
      // Format and emit dates for parent component
      const payload = {
        fromDate: format(startDate, 'yyyy-MM-dd'),
        toDate: format(endDate, 'yyyy-MM-dd'),
      };
      
      // Call onChange to trigger the same flow as predefined selections
      this.onChange(payload);
    },
    formatDateForUrl(dateString) {
      // Convert from yyyy-MM-dd to dd.MM.yyyy
      const [year, month, day] = dateString.split('-');
      return `${day}.${month}.${year}`;
    },
    async downloadReport(reportConfig) {
      try {
        this.isDownloadingReport = true;
        
        // Show toast notification
        this.getToaster('info', this.$t('flexigo.dashboard.reportPreparing'), '');

        const { reportType, params = {} } = reportConfig;

        // Step 1: Get the fileId from the report endpoint
        const reportResponse = await reports.getReportByType(`/report/${reportType}`, params);

        if (reportResponse?.response?.fileId) {
          const { fileId } = reportResponse.response;

          const BASEURL = process.env.NODE_ENV === 'development' ? '/flexigo' : '/commute';
          const path = window.location.origin.includes('localhost')
            ? 'http://localhost:5431'
            : ` ${window.location.origin}${BASEURL}`;
          const url = `${path}/doc/report/${fileId}`.replace('/index.html', '');

          // Step 2: Construct the download URL and open it

          window.open(url, '_blank');
        } else {
          console.error('No fileId returned from report endpoint');
        }
      } catch (error) {
        console.error(`Error downloading ${reportConfig.reportType} report:`, error);
      } finally {
        this.isDownloadingReport = false;
      }
    },
  },
};
</script>

<style></style>
